import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { langueCourante, traduire } from '@/i18n/language'

type Props = { screenName: string; children: ReactNode }
type State = { failed: boolean }

/**
 * Isole le rejet d'un chunk ou l'exception d'un écran de la coquille applicative.
 * Le détail technique est volontairement ignoré : il peut contenir une URL signée,
 * un chemin interne ou le corps d'une réponse.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // La télémétrie, si elle existe, est branchée hors de ce composant. Ne jamais
    // rendre ni sérialiser l'exception brute dans le navigateur.
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children
    // Composant de classe : pas de hook ; la langue courante est lue au rendu de l'échec.
    const lang = langueCourante()
    const t = (cle: string, repli: string, v?: Record<string, string>): string => traduire(lang, cle, repli, v)
    const ecran = { ecran: this.props.screenName }
    return (
      <section className="state state--error" role="alert" aria-label={t('echecEcran.region', 'Échec de {{ecran}}', ecran)}>
        <p className="state__title">{t('echecEcran.titre', 'Impossible d’ouvrir {{ecran}}.', ecran)}</p>
        <p className="state__hint">{t('echecEcran.indice', 'La navigation reste disponible. Réessayez ou revenez au tableau de bord.')}</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="button" onClick={() => this.setState({ failed: false })}>
            {t('echecEcran.reessayer', 'Réessayer')}
          </button>
          <Link className="button" to="/">{t('acces.retourTableauDeBord', 'Retour au tableau de bord')}</Link>
        </div>
      </section>
    )
  }
}
